import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {toast} from 'sonner'

import {StorageService} from '../service/storage.services'
import {TGetUsersOptions, UserService} from '../service/user.services'
import {TInviteUser, TUpdateUser, TUser} from '../types/user.type'
import {getTranslation} from '../utils/getTranslation'

export function useGetMe(): TUser | undefined {
  const userId = StorageService.getUserId()

  const {data: me} = useQuery({
    queryKey: ['me', userId],
    queryFn: () => UserService.getUserById(userId),
    // Session-stable: only changes on logout/login. Invalidate on profile updates if added.
    staleTime: Infinity,
  })

  return me
}

export function useGetUsers(
  clientId: string,
  page: number,
  options: TGetUsersOptions = {},
) {
  const {data, isPending, isSuccess} = useQuery({
    queryKey: ['users', clientId, page, options],
    queryFn: () => UserService.getUsers(clientId, page, 50, options),
    enabled: !!clientId,
    // Volatile: list changes whenever a user is added/edited/removed or
    // activates their account; the user mutations invalidate ['users'].
    staleTime: 0,
  })

  return {data, isPending, isSuccess}
}

/**
 * Roster for the activity-list requestor filter: client users who can
 * create/request activities (everyone except VIEWER — REQUESTOR + OPC + ADMIN).
 * Used only to populate the filter's option list.
 */
export function useGetRequestorOptions(clientId: string) {
  return useQuery({
    queryKey: ['users', 'requestorOptions', clientId],
    queryFn: () => UserService.getRequestorOptions(clientId),
    enabled: !!clientId,
    staleTime: Infinity,
  })
}

export function useGetClientUserStats(clientId: string) {
  return useQuery({
    queryKey: ['userStats', clientId],
    queryFn: () => UserService.getClientUserStats(clientId),
    enabled: !!clientId,
    // Volatile-safe: changes whenever a user is added/edited/removed or activates
    // their account. Invalidated by the user mutations in UserModal.
    staleTime: 0,
  })
}

export function useCreateUser() {
  const {
    mutateAsync: createUser,
    isPending,
    error,
  } = useMutation({
    mutationFn: (userData: TInviteUser) => UserService.createUser(userData),
  })

  return {createUser, isPending, error}
}

export function useUpdateUser(userId: string) {
  const {
    mutateAsync: updateUser,
    isPending,
    error,
  } = useMutation({
    mutationFn: (user: TUpdateUser) => UserService.updateUser(userId, user),
  })

  return {updateUser, isPending, error}
}

export function useDeleteUser() {
  const {
    mutate: deleteUser,
    isPending,
    isSuccess,
    error,
  } = useMutation({
    mutationFn: ({userId, clientId}: {userId: string; clientId: string}) =>
      UserService.deleteUser(userId, clientId),
  })

  return {deleteUser, isPending, isSuccess, error}
}

export function useResendInvitation() {
  const queryClient = useQueryClient()
  const {
    mutate: resendInvitation,
    isPending,
    error,
  } = useMutation({
    mutationFn: ({userId, clientId}: {userId: string; clientId: string}) =>
      UserService.resendInvitation(userId, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['users']})
      toast.success(getTranslation('userList:resendInvitationSuccess'))
    },
    onError: () => {
      toast.error(getTranslation('userList:resendInvitationError'))
    },
  })

  return {resendInvitation, isPending, error}
}
